import { z } from 'zod';
import { visualizationTypeSchema } from './visualization-dto';

// Export formats
export const exportFormatSchema = z.enum(['png', 'svg', 'pdf']);
export type ExportFormat = z.infer<typeof exportFormatSchema>;

// Export options per format
export const pngOptionsSchema = z.object({
  // Background
  transparentBackground: z.boolean().default(false),

  // Layout options
  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  showLegend: z.boolean().default(true),

  // Content options
  visionStatement: z
    .string()
    .max(1000, 'Vision statement cannot exceed 1000 characters')
    .optional()
    .transform((val) => val?.trim() || undefined), // Trim and convert empty to undefined

  // Export resolution (viewport width in pixels, height is auto-calculated)
  viewportWidth: z.number().int().positive().optional(),
});
export type PngOptions = z.infer<typeof pngOptionsSchema>;

export const svgOptionsSchema = z.object({
  embedFonts: z.boolean().default(true),
});
export type SvgOptions = z.infer<typeof svgOptionsSchema>;

export const pdfOptionsSchema = z.object({
  useStandardPageSize: z.boolean().default(false),
  pageSize: z.enum(['A4', 'Letter', 'Legal', 'Slide16x9']).default('Letter'),
  orientation: z.enum(['portrait', 'landscape']).default('landscape'),

  // Layout options (shared with PNG)
  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  showLegend: z.boolean().default(true),

  // Content options
  visionStatement: z
    .string()
    .max(1000, 'Vision statement cannot exceed 1000 characters')
    .optional()
    .transform((val) => val?.trim() || undefined), // Trim and convert empty to undefined

  // Timeline-only: resolved viewport dimensions from Dimension+Resolution presets
  timelineWidth: z.number().int().min(595).max(4200).optional(),
  timelineHeight: z.union([z.number().int().min(595).max(4200), z.literal('auto')]).optional(),
});
export type PdfOptions = z.infer<typeof pdfOptionsSchema>;

export const exportOptionsSchema = z.object({
  png: pngOptionsSchema.optional(),
  svg: svgOptionsSchema.optional(),
  pdf: pdfOptionsSchema.optional(),
});
export type ExportOptions = z.infer<typeof exportOptionsSchema>;

// Export task states
export const exportTaskStateSchema = z.enum([
  'pending',
  'processing',
  'succeeded',
  'failed',
  'too_large',
  'expired',
]);
export type ExportTaskState = z.infer<typeof exportTaskStateSchema>;

// SQS message schema
export const exportMessageSchema = z.object({
  taskId: z.number().int(),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  organizationId: z.string().min(1),
  exportToken: z.string(),
  format: exportFormatSchema,
  options: exportOptionsSchema.optional(),
  retryCount: z.number().int().default(0),
  visualizationType: visualizationTypeSchema.default('swimlane'),
});
export type ExportMessage = z.infer<typeof exportMessageSchema>;

// API request/response schemas

// Create export request
export const createExportRequestSchema = z.object({
  format: exportFormatSchema,
  options: exportOptionsSchema.optional(),
});
export type CreateExportRequest = z.infer<typeof createExportRequestSchema>;

// Export task DTO (returned from API)
export const exportTaskDTOSchema = z.object({
  taskId: z.number().int(),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  organizationId: z.string(),
  format: exportFormatSchema,
  state: exportTaskStateSchema,
  options: exportOptionsSchema.optional(),
  s3Key: z.string().nullable(),
  error: z.string().nullable(),
  downloaded: z.boolean(),
  downloadedAt: z.iso.datetime().nullable(),
  retryCount: z.number().int(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
});
export type ExportTaskDTO = z.infer<typeof exportTaskDTOSchema>;

// Export status response (lightweight version for polling)
export const exportStatusDTOSchema = z.object({
  taskId: z.number().int(),
  format: exportFormatSchema,
  state: exportTaskStateSchema,
  error: z.string().nullable(),
  downloaded: z.boolean(),
  retryCount: z.number().int(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});
export type ExportStatusDTO = z.infer<typeof exportStatusDTOSchema>;
